/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useEffect,
  useContext,
  createContext,
  ReactNode,
  useReducer,
} from 'react';
import { DatabaseTools } from '@mummssoftware/utils';
import {
  SET_PRINT_INTERFACE_OPEN,
  SET_PRINT_LAYOUT_OPEN,
  SET_PRINT_CONFIG,
  ADD_WIDGET_READY,
  RESET_WIDGETS_READY,
  SET_LOADER,
  SET_ALERT,
  SET_THEME_OVERLAY,
  REFRESH_DASHBOARD,
  SET_DISABLE_PRINT,
  SET_DASHBOARD_READY,
  pouchDBName,
} from '../constants';
import { Context } from '../../../context';

type DashboardContextProviderProps = {
  children: ReactNode;
};

type DashboardContext = {
  printInterfaceOpen: boolean | null;
  printLayoutOpen: boolean | null;
  loader: boolean | null;
  refreshDashboard: number;
  alertInfo: mumms.ErrorInfo | null;
  themeOverlay: boolean;
  widgetsReady: mumms.ComponentName[] | string[];
  dashboardPrintConfig: mumms.PrintConfigInterface | null;
  database: DatabaseTools | null;
  disablePrintIcon: boolean;
  dashboardReady: boolean;
  dispatch?: any;
};

type DashboardContextState = {
  printInterfaceOpen: boolean | null;
  printLayoutOpen: boolean | null;
  loader: boolean | null;
  refreshDashboard: number;
  alertInfo: mumms.ErrorInfo | null;
  themeOverlay: boolean;
  widgetsReady: mumms.ComponentName[] | string[];
  dashboardPrintConfig: mumms.PrintConfigInterface | null;
  database: DatabaseTools | null;
  disablePrintIcon: boolean;
  dashboardReady: boolean;
  dispatch?: any;
};

const initialState = {
  printInterfaceOpen: null,
  printLayoutOpen: null,
  loader: null,
  dashboardPrintConfig: null,
  refreshDashboard: 0,
  alertInfo: null,
  themeOverlay: false,
  widgetsReady: [],
  database: null,
  disablePrintIcon: true,
  dashboardReady: false,
};

const initDashboardContext = {
  printInterfaceOpen: null,
  printLayoutOpen: null,
  loader: null,
  dashboardPrintConfig: null,
  refreshDashboard: 0,
  alertInfo: null,
  themeOverlay: false,
  widgetsReady: [],
  database: null,
  disablePrintIcon: true,
  dashboardReady: false,
};

export const DashboardContextReducer = (
  state: DashboardContextState,
  action: { type: string; payload: mumms.ComponentName | any },
) => {
  let newState = { ...state };
  switch (action.type) {
    case SET_PRINT_INTERFACE_OPEN:
      newState.printInterfaceOpen = action.payload;
      break;
    case SET_PRINT_CONFIG:
      newState.dashboardPrintConfig = action.payload;
      break;
    case SET_LOADER:
      newState.loader = action.payload;
      break;
    case ADD_WIDGET_READY:
      newState.widgetsReady.push(action.payload);
      break;
    case RESET_WIDGETS_READY:
      newState.widgetsReady = [];
      break;
    case SET_ALERT:
      newState.alertInfo = action.payload;
      break;
    case SET_PRINT_LAYOUT_OPEN:
      newState.printLayoutOpen = action.payload;
      break;
    case SET_THEME_OVERLAY:
      newState.themeOverlay = action.payload;
      break;
    case REFRESH_DASHBOARD:
      newState.refreshDashboard += 1;
      break;
    case SET_DISABLE_PRINT:
      newState.disablePrintIcon = action.payload;
      break;
    case SET_DASHBOARD_READY:
      newState.dashboardReady = action.payload;
      break;
    default:
      newState = state;
      break;
  }
  return newState;
};

export const DashboardContext = createContext<DashboardContext>(
  initDashboardContext as DashboardContext,
);
export const DashboardContextProvider = ({
  children,
}: DashboardContextProviderProps) => {
  const [
    {
      printInterfaceOpen,
      printLayoutOpen,
      dashboardPrintConfig,
      widgetsReady,
      loader,
      alertInfo,
      themeOverlay,
      refreshDashboard,
      disablePrintIcon,
      dashboardReady,
    },
    dispatch,
  ] = useReducer(DashboardContextReducer, initialState);
  const { currentUser, keycloak, patient, agency } = useContext(Context);

  const [db, setDB] = useState<any>(null);

  // on Mount init database
  useEffect(() => {
    if (patient) {
      const pn = currentUser.personNumber as string;
      const newDB = new DatabaseTools(
        agency,
        pn,
        keycloak,
        patient.patientNumber,
      );
      setDB(newDB);
    }
  }, []);

  // reset database if issue with remote DB
  useEffect(() => {
    if (patient && db && db.localDB.name === pouchDBName) {
      const pn = currentUser.personNumber as string;
      const newDB = new DatabaseTools(
        agency,
        pn,
        keycloak,
        patient.patientNumber,
      );
      setDB(newDB);
    }
  }, [db]);
  // todo useMemo
  return (
    <DashboardContext.Provider
      value={{
        printInterfaceOpen,
        printLayoutOpen,
        dashboardPrintConfig,
        widgetsReady,
        loader,
        alertInfo,
        themeOverlay,
        refreshDashboard,
        database: db,
        disablePrintIcon,
        dashboardReady,
        dispatch,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardContextProvider;
